import { useLocation } from 'react-router'
import { CategoryChip } from '../../platform/primitives/CategoryChip'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { DataGrid, type Column, type Group, type Selection } from '../../platform/primitives/DataGrid'
import { MerchantAvatar } from '../../platform/primitives/MerchantAvatar'
import { Money } from '../../platform/primitives/Money'
import type { CategoryListStatus } from '../categories/setCategoryLabel'
import { CategoryPill } from './CategoryPill'
import { MerchantPill, needsMerchantPill } from './MerchantPill'
import type { Txn } from './transactionWire'
import { accountLabel, avatarOf, shortDate } from './txnDisplay'
import type { Account } from '../accounts/useAccounts'
import './transactions.css'

type Props = {
  groups: Group<Txn>[]
  accounts: ReadonlyMap<string, Pick<Account, 'name' | 'mask'>>
  categories: ReadonlyMap<string, string>
  /** The categories list's state, so a pill names a set id it cannot name yet as "Loading", not "Unknown" (R7). */
  categoriesStatus: CategoryListStatus
  /** Opt-in selection; the screen owns the state, so Task 9's bulk toolbar can attach to it. */
  selection?: Selection<Txn>
  /** Shown, under the column headers, when no group has rows. */
  empty: React.ReactNode
  /** What a row link tells the detail page to go back to: a `?search` (the /transactions list) or a `/cashflow/…` URL. Defaults to this page's own search. */
  backFrom?: string
  /** Given, each row's category pill is a button that opens the picker; absent, the pill is plain text as before. */
  onCategoryClick?: (t: Txn, opener: HTMLButtonElement) => void
  /** Rows only: the day bands are drawn WITHOUT their subtotals (stripped here, whatever `groups` carries) and nothing reconciles. Default off. */
  plain?: boolean
  /** Given, a row with no merchant shows "Needs merchant" under its name, which opens the merchant picker; absent, the row is as before. */
  onMerchantClick?: (t: Txn, opener: HTMLButtonElement) => void
}

/**
 * The desktop and tablet list. Columns per the render: Merchant, Category,
 * Account, Owner, Date, Amount. `minmax(0, …)` tracks, so a long merchant name
 * truncates rather than pushing the amount off screen.
 *
 * Reconciliation is **on**: `amountCents` and `rowCurrency` are passed, so a day
 * band whose subtotal its own rows contradict throws rather than rendering a
 * plausible wrong figure. The page is complete for each day it shows only by
 * construction of `dayGroups`, which subtotals exactly the rows it was given.
 */
export function TransactionGrid({ groups, accounts, categories, categoriesStatus, selection, empty, backFrom, plain = false, onCategoryClick, onMerchantClick }: Props) {
  const { search } = useLocation()
  const from = backFrom ?? search

  const columns: Column<Txn>[] = [
    {
      key: 'merchant',
      header: 'Merchant',
      width: 'minmax(0, 1.6fr)',
      render: (t) => {
        const pill = onMerchantClick !== undefined && needsMerchantPill(t)
        return (
          <span className={pill ? 'txn-merchant txn-merchant--needs' : 'txn-merchant'}>
            <MerchantAvatar merchant={avatarOf(t)} />
            {/* An `InlineLink`, so the hit-target harness reads it as the inline link it is
                (24px target) rather than measuring it at the 33px page floor. The name is its
                own span: the link is a flex box, and only a block can ellipsise its text. */}
            <InlineLink to={`/transactions/${t.id}`} state={{ from }} className="txn-merchant__link">
              <span className="txn-merchant__name">{t.merchant}</span>
            </InlineLink>
            {t.isPending && <span className="txn-chip">Pending</span>}
            {pill && (
              // "Needs merchant" on its own line under the name: only this row grows, and only by the pill.
              <span className="txn-merchant__needs">
                <MerchantPill t={t} onMerchantClick={onMerchantClick} />
              </span>
            )}
          </span>
        )
      },
    },
    {
      key: 'category',
      header: 'Category',
      width: 'minmax(0, 1.1fr)',
      render: (t) => {
        const name = t.categoryId === null ? undefined : categories.get(t.categoryId)
        // "Needs category" is the row's own state (Q1), shown in words; a
        // category id this client has no name for yet is not that state and
        // reads as a dash rather than as an accusation.
        return onCategoryClick !== undefined ? (
            <CategoryPill t={t} name={name} status={categoriesStatus} onCategoryClick={onCategoryClick} />
          ) : t.categoryId === null ? (
            <span className="txn-chip txn-chip--warn">Needs category</span>
          ) : name === undefined ? (
            <span className="txn-muted">—</span>
          ) : (
            <CategoryChip name={name} size="chip" />
          )
      },
    },
    {
      key: 'account',
      header: 'Account',
      width: 'minmax(0, 1.1fr)',
      render: (t) => {
        const a = accounts.get(t.accountId)
        return <span className="txn-ellipsis txn-secondary">{a ? accountLabel(a) : '—'}</span>
      },
    },
    {
      key: 'owner',
      header: 'Owner',
      width: 'minmax(0, 0.8fr)',
      render: (t) => <span className="txn-ellipsis txn-secondary">{t.ownerName ?? '—'}</span>,
    },
    { key: 'date', header: 'Date', width: 'minmax(0, 0.8fr)', render: (t) => <span className="txn-secondary">{shortDate(t.date)}</span> },
    {
      key: 'amount',
      header: 'Amount',
      width: '120px',
      align: 'end',
      render: (t) => (
        <span className="txn-amount">
          <Money cents={t.amountCents} currency={t.currency} kind="flow" />
        </span>
      ),
    },
  ]

  return (
    <DataGrid
      label="Transactions"
      columns={columns}
      groups={plain ? groups.map(({ label, rows }) => ({ label, rows })) : groups}
      rowKey={(t) => t.id}
      empty={empty}
      amountCents={plain ? undefined : (t) => t.amountCents}
      rowCurrency={plain ? undefined : (t) => t.currency}
      stickyBands
      selection={selection}
      rowClassName={(t) => (t.categoryId === null ? 'txn-row--uncategorised' : undefined)}
    />
  )
}
